import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { authApi, UserStats } from '@/lib/api';
import { Button } from '@/components/common';
import { ThinPage } from '@/components/common/PageQuality';
import SEO from '@/components/common/SEO';
import {
  getTitleForLevel,
  getProgressToNextLevel,
  getPointsForLevel,
  getPointsForNextLevel,
  getAvatarPreset,
  formatPoints,
} from '@/lib/gamification';
import { useEmailPreferences, useUpdateEmailPreferences } from '@/hooks/useEmailPreferences';
import { Mail, Bell, Loader2 } from 'lucide-react';
import EditProfileModal from './EditProfileModal';

// Email Preferences Section Component
function EmailPreferencesSection() {
  const { data: prefs, isLoading } = useEmailPreferences();
  const updatePrefs = useUpdateEmailPreferences();

  const handleToggle = (key: string, value: boolean) => {
    updatePrefs.mutate({ [key]: value });
  };

  const handleFrequencyChange = (frequency: 'daily' | 'weekly' | 'never') => {
    updatePrefs.mutate({
      digestFrequency: frequency,
      digestEnabled: frequency !== 'never',
    });
  };

  if (isLoading) {
    return (
      <div className="mt-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-coral-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Mail className="w-5 h-5 text-coral-500" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Email Preferences
        </h2>
      </div>

      {/* Digest Frequency */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Community Digest
        </label>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
          Receive updates about trending posts, your activity, and community highlights.
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { value: 'daily', label: 'Daily' },
            { value: 'weekly', label: 'Weekly' },
            { value: 'never', label: 'Never' },
          ].map((option) => (
            <button
              key={option.value}
              onClick={() => handleFrequencyChange(option.value as 'daily' | 'weekly' | 'never')}
              disabled={updatePrefs.isPending}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                prefs?.digestFrequency === option.value || (!prefs?.digestEnabled && option.value === 'never')
                  ? 'bg-coral-500 text-white'
                  : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Digest Content Options */}
      {prefs?.digestEnabled && (
        <div className="mb-6 pl-4 border-l-2 border-coral-200 dark:border-coral-800">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Include in digest:
          </p>
          <div className="space-y-2">
            {[
              { key: 'includeHotPosts', label: 'Trending posts', value: prefs?.includeHotPosts },
              { key: 'includeEngagementSummary', label: 'Your activity summary', value: prefs?.includeEngagementSummary },
              { key: 'includePointsProgress', label: 'Points & level progress', value: prefs?.includePointsProgress },
            ].map((item) => (
              <label key={item.key} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={item.value ?? true}
                  onChange={(e) => handleToggle(item.key, e.target.checked)}
                  disabled={updatePrefs.isPending}
                  className="w-4 h-4 rounded border-gray-300 text-coral-500 focus:ring-coral-500"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">{item.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Notification Emails */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-4 h-4 text-gray-500" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Email notifications
          </p>
        </div>
        <div className="space-y-2">
          {[
            { key: 'notifyComments', label: 'When someone comments on my post', value: prefs?.notifyComments },
            { key: 'notifyLikes', label: 'When someone likes my post', value: prefs?.notifyLikes },
            { key: 'notifyReplies', label: 'When someone replies to my comment', value: prefs?.notifyReplies },
            { key: 'notifyMentions', label: 'When someone mentions me', value: prefs?.notifyMentions },
          ].map((item) => (
            <label key={item.key} className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={item.value ?? true}
                onChange={(e) => handleToggle(item.key, e.target.checked)}
                disabled={updatePrefs.isPending}
                className="w-4 h-4 rounded border-gray-300 text-coral-500 focus:ring-coral-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">{item.label}</span>
            </label>
          ))}
        </div>
      </div>

      {updatePrefs.isPending && (
        <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Saving...
        </div>
      )}
    </div>
  );
}

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { user } = useAuthStore();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadStats();
    }
  }, [user]);

  const loadStats = async () => {
    try {
      const data = await authApi.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="px-8 py-8 text-center">
        {/* Logged-out view of a private page — nothing here to index. */}
        <SEO title="Your profile" description="Sign in to view your KoreanHub profile." noindex />
        <ThinPage reason="profile page viewed logged-out" />
        <p className="text-gray-500">Please log in to view your profile.</p>
      </div>
    );
  }

  // Defensive defaults for user data
  const userLevel = user.level || 1;
  const userPoints = user.points || 0;
  const userTokens = user.tokens || 0;

  const title = getTitleForLevel(userLevel);
  const progress = getProgressToNextLevel(userPoints, userLevel);
  const nextThreshold = getPointsForNextLevel(userLevel);
  const avatar = getAvatarPreset(user.avatarPreset);
  const isKorean = i18n.language === 'ko';

  const formatMemberSince = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isKorean ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  return (
    <div className="px-4 py-8 max-w-2xl mx-auto">
      {/*
        The signed-in user's own account page. It is personalised, gated, and has
        no public value — never index it, regardless of how complete it looks.
        SEO still supplies a title so the prerendered HTML isn't titleless.
      */}
      <SEO
        title="Your profile"
        description="Manage your KoreanHub profile, activity, and notification preferences."
        noindex
      />
      <ThinPage reason="private account page" />

      {/* Profile Header Card */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg overflow-hidden">
        {/* Gradient Banner */}
        <div className="h-24 bg-gradient-to-r from-coral-400 via-coral-500 to-coral-600" />

        {/* Profile Info */}
        <div className="px-6 pb-6">
          {/* Avatar */}
          <div className="-mt-12 mb-4 flex items-end justify-between">
            <div className="w-24 h-24 rounded-full bg-white dark:bg-slate-800 border-4 border-white dark:border-slate-800 shadow-lg flex items-center justify-center text-5xl">
              {avatar.emoji}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setIsEditModalOpen(true)}
            >
              Edit Profile
            </Button>
          </div>

          {/* Name and Title */}
          <div className="mb-4">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {user.name}
              {user.nameKo && (
                <span className="ml-2 text-lg font-normal text-gray-500 dark:text-gray-400">
                  ({user.nameKo})
                </span>
              )}
            </h1>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-gray-500 dark:text-gray-400">{user.email}</span>
              <span className="text-gray-300 dark:text-gray-600">|</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm font-medium bg-coral-100 dark:bg-coral-900/30 text-coral-700 dark:text-coral-300">
                {title.icon} {isKorean ? title.korean : title.english}
              </span>
            </div>
            {stats && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Member since {formatMemberSince(stats.memberSince)}
              </p>
            )}
          </div>

          {/* Bio */}
          {user.bio && (
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {user.bio}
            </p>
          )}

          {/* Location */}
          {user.location && (
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>{user.location}</span>
            </div>
          )}
        </div>
      </div>

      {/* Level Progress Card */}
      <div className="mt-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Level Progress
        </h2>

        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold text-coral-600">Lv.{userLevel}</span>
            <span className="text-gray-500 dark:text-gray-400">
              {isKorean ? title.korean : title.english}
            </span>
          </div>
          {userLevel < 10 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Lv.{userLevel + 1} {getTitleForLevel(userLevel + 1).icon}
            </span>
          )}
        </div>

        {/* Progress Bar */}
        <div className="relative h-4 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden mb-2">
          <div
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-coral-400 to-coral-600 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="flex justify-between text-sm">
          <span className="text-gray-600 dark:text-gray-300">
            {formatPoints(userPoints)} points
          </span>
          {userLevel < 10 ? (
            <span className="text-gray-500 dark:text-gray-400">
              {formatPoints(nextThreshold - userPoints)} to next level
            </span>
          ) : (
            <span className="text-coral-600 font-medium">Max Level!</span>
          )}
        </div>

        {/* Points and Tokens */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
            <div className="text-2xl font-bold text-coral-600">{formatPoints(userPoints)}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Total Points</div>
          </div>
          <div className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
            <div className="text-2xl font-bold text-amber-500">{formatPoints(userTokens)}</div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Tokens</div>
          </div>
        </div>
      </div>

      {/* Contributions Card */}
      <div className="mt-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Contributions
        </h2>

        {statsLoading ? (
          <div className="grid grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl animate-pulse">
                <div className="h-8 bg-gray-200 dark:bg-slate-600 rounded mb-2" />
                <div className="h-4 bg-gray-200 dark:bg-slate-600 rounded w-12 mx-auto" />
              </div>
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
              <div className="text-2xl font-bold text-blue-600">{stats.postsCount}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Posts</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
              <div className="text-2xl font-bold text-green-600">{stats.commentsCount}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Comments</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
              <div className="text-2xl font-bold text-purple-600">{stats.reviewsCount}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Reviews</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-slate-700 rounded-xl">
              <div className="text-2xl font-bold text-red-500">{stats.likesReceived}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Likes</div>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">Failed to load stats</p>
        )}
      </div>

      {/* Title Progression Card */}
      <div className="mt-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Title Progression
        </h2>

        <div className="space-y-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => {
            const levelTitle = getTitleForLevel(level);
            const threshold = getPointsForLevel(level);
            const isCurrentLevel = level === userLevel;
            const isAchieved = userLevel >= level;

            return (
              <div
                key={level}
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  isCurrentLevel
                    ? 'bg-coral-100 dark:bg-coral-900/30 ring-1 ring-coral-500'
                    : isAchieved
                    ? 'bg-gray-50 dark:bg-slate-700'
                    : 'bg-gray-50/50 dark:bg-slate-700/50 opacity-60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">{levelTitle.icon}</span>
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      Lv.{level} {isKorean ? levelTitle.korean : levelTitle.english}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {isKorean ? levelTitle.english : levelTitle.korean}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {formatPoints(threshold)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">points</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Email Preferences Card */}
      <EmailPreferencesSection />

      {/* Edit Profile Modal */}
      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        user={user}
      />
    </div>
  );
}
